<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores';
import { useDropdown } from '@/composables/useDropdown';
import type { UserRole } from '@/types';

const userStore = useUserStore();
const router = useRouter();
const { isOpen, toggle, close } = useDropdown('user-dropdown');

const initials = computed(() => userStore.initials);
const fullName = computed(() => userStore.fullName);
const email = computed(() => userStore.profile?.email ?? '');
const organization = computed(() => userStore.profile?.organization ?? '');
const userRole = computed(() => userStore.profile?.role ?? 'learner');
const canBeSuperAdmin = computed(() => userStore.canBeSuperAdmin);
const canSwitchRoles = computed(() => userStore.canSwitchRoles);

const switchRole = (role: UserRole) => {
  userStore.setRole(role);
  close();
  // Navigate to the appropriate dashboard
  if (role === 'super_admin') {
    router.push('/super-admin');
  } else if (role === 'org_admin') {
    router.push('/admin');
  } else {
    router.push('/');
  }
};
</script>

<template>
  <div class="user-menu-container">
    <button
      class="icon-btn user-profile"
      id="user-menu-toggle"
      aria-label="User profile"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      aria-controls="user-dropdown"
      @click.stop="toggle($event)"
    >
      <div class="user-avatar-small">{{ initials }}</div>
    </button>
    <div
      class="user-dropdown"
      id="user-dropdown"
      role="menu"
      aria-labelledby="user-menu-toggle"
      :class="{ show: isOpen }"
    >
      <div class="user-dropdown-header">
        <div class="user-dropdown-name">{{ fullName }}</div>
        <div class="user-dropdown-email">{{ email }}</div>
        <div class="user-dropdown-org">{{ organization }}</div>
      </div>
      <RouterLink to="/settings" class="dropdown-item" role="menuitem" @click="close()">
        <bb-icon name="person" decorative></bb-icon>
        <span>My Profile</span>
      </RouterLink>
      <RouterLink to="/activity" class="dropdown-item" role="menuitem" @click="close()">
        <bb-icon name="history" decorative></bb-icon>
        <span>My Activity</span>
      </RouterLink>
      <div class="dropdown-divider" role="separator"></div>
      <div v-if="canSwitchRoles" class="role-switcher">
        <div class="role-switcher-label">Switch Role</div>
        <div class="role-switcher-buttons">
          <button
            class="role-btn"
            :class="{ active: userRole === 'learner' }"
            role="menuitem"
            @click="switchRole('learner')"
          >
            Learner
          </button>
          <button
            class="role-btn"
            :class="{ active: userRole === 'org_admin' }"
            role="menuitem"
            @click="switchRole('org_admin')"
          >
            Admin
          </button>
          <button
            v-if="canBeSuperAdmin"
            class="role-btn"
            :class="{ active: userRole === 'super_admin' }"
            role="menuitem"
            @click="switchRole('super_admin')"
          >
            Super
          </button>
        </div>
      </div>
      <div class="dropdown-divider" role="separator"></div>
      <RouterLink to="/logout" class="dropdown-item" role="menuitem" @click="close()">
        <bb-icon name="logout" decorative></bb-icon>
        <span>Sign Out</span>
      </RouterLink>
    </div>
  </div>
</template>

<style scoped>
.user-dropdown-org {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-top: 4px;
}

.role-switcher {
  padding: 8px 16px;
}

.role-switcher-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.role-switcher-buttons {
  display: flex;
  gap: 4px;
}

.role-btn {
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--color-border-default);
  border-radius: 4px;
  background: var(--color-bg-card);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.role-btn:hover {
  background: var(--color-bg-hover);
  border-color: var(--color-border-dark);
}

.role-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}
</style>
